import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineXCircle,
  HiOutlineArrowPath,
  HiOutlineXMark,
  HiOutlineCreditCard,
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineUserGroup,
  HiOutlineServer,
  HiOutlineCog
} from 'react-icons/hi2';
import { subscriptionService, authService } from '../services';
import { usePermissions } from '../hooks/usePermissions';
import SharedModal from '../components/common/SharedModal';
import PageLoading from "../components/common/PageLoading";
import './Subscription.css';

const Subscription = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  // Permission checks
  const canView = hasPermission('Subscription.View');
  const canManage = hasPermission('Subscription.Manage');
  
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [changing, setChanging] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId && canView) {
      loadSubscriptionData();
    } else if (!canView) {
      setError('You do not have permission to view subscription details');
      setLoading(false);
    } else {
      setError('Please login to view subscription');
      setLoading(false);
    }
  }, [tenantId, canView]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError('');
      const [subscription, usage, plans] = await Promise.all([
        subscriptionService.getCurrentSubscription(tenantId),
        subscriptionService.getUsageSummary(tenantId),
        subscriptionService.getPlans()
      ]);
      setCurrentSubscription(subscription);
      setUsageSummary(usage);
      setAvailablePlans(plans);
    } catch (err) {
      setError('Failed to load subscription data. Please try again.');
      console.error('Error loading subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!canManage) {
      setError('You do not have permission to manage subscriptions');
      return;
    }
    
    if (!selectedPlanId) {
      setError('Please select a plan');
      return;
    }

    try {
      setChanging(true);
      setError('');

      // Check if change is allowed
      const canChange = await subscriptionService.canChangeToPlan(tenantId, selectedPlanId);
      if (!canChange.canChange) {
        setError(canChange.message || 'Cannot change to this plan. Current usage exceeds the new plan limits.');
        setChanging(false);
        return;
      }

      // Proceed with change
      await subscriptionService.changeSubscription(tenantId, selectedPlanId);
      await loadSubscriptionData();
      setShowChangePlanModal(false);
      setSelectedPlanId(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to change subscription plan. Please try again.';
      setError(errorMessage);
    } finally {
      setChanging(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!canManage) {
      setError('You do not have permission to manage subscriptions');
      return;
    }
    
    try {
      setCancelling(true);
      setError('');
      await subscriptionService.cancelSubscription(tenantId);
      await loadSubscriptionData();
      setShowCancelModal(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to cancel subscription. Please try again.';
      setError(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  const getUsagePercentage = (current, max) => {
    if (max === 0) return 0; // Unlimited
    return Math.min((current / max) * 100, 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="pagecontainer subscription-page">
        <PageLoading message="Loading subscription..." />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="pagecontainer subscription-page">
        <div className="error-message" style={{ 
          textAlign: 'center', 
          padding: '2rem',
          fontSize: '1.1rem',
          color: '#d93025'
        }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view subscription details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pagecontainer subscription-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Subscription Management</h1>
          <p>Manage your subscription plan and view usage</p>
        </div>
        {currentSubscription?.hasSubscription && (
          <button
            className="btn-secondary"
            onClick={() => navigate('/purchase')}
          >
            <HiOutlineCreditCard className="btn-icon" />
            View All Plans
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {!currentSubscription?.hasSubscription ? (
        <div className="no-subscription">
          <HiOutlineXCircle className="no-sub-icon" />
          <h3>No Active Subscription</h3>
          <p>You don't have an active subscription plan. Choose a plan to get started.</p>
          <button
            className="btn-primary"
            onClick={() => navigate('/purchase')}
          >
            Browse Plans
          </button>
        </div>
      ) : (
        <>
          {/* Current Subscription Card */}
          <div className="subscription-card">
            <div className="subscription-header">
              <div className='flexbox'>
                <h3>{currentSubscription.plan?.planName || 'No Plan'}</h3>
                <p className="plan-code">{currentSubscription.plan?.planCode}</p>
              </div>
              <div className="subscription-status">
                <span className={`status-badge ${currentSubscription.isActive ? 'active' : 'inactive'}`}>
                  {currentSubscription.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="subscription-details">
              {usageSummary?.subscriptionStartDate && (
                <div className="detail-item">
                  <HiOutlineCalendar className="detail-icon" />
                  <div>
                    <label>Start Date</label>
                    <p>{formatDate(usageSummary.subscriptionStartDate)}</p>
                  </div>
                </div>
              )}
              {usageSummary?.subscriptionEndDate && (
                <div className="detail-item">
                  <HiOutlineCalendar className="detail-icon" />
                  <div>
                    <label>End Date</label>
                    <p>{formatDate(usageSummary.subscriptionEndDate)}</p>
                  </div>
                </div>
              )}
              <div className="detail-item">
                <HiOutlineCreditCard className="detail-icon" />
                <div>
                  <label>Billing Cycle</label>
                  <p>{currentSubscription.plan?.billingCycle || 'N/A'}</p>
                </div>
              </div>
              <div className="detail-item">
                <HiOutlineCreditCard className="detail-icon" />
                <div>
                  <label>Price</label>
                  <p>{formatCurrency(currentSubscription.plan?.price || 0, currentSubscription.plan?.currency || 'USD')}</p>
                </div>
              </div>
            </div>

            {canManage && (
              <div className="subscription-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setShowChangePlanModal(true)}
                >
                  <HiOutlineArrowPath className="btn-icon" />
                  Change Plan
                </button>
                <button
                  className="btn-danger"
                  onClick={() => setShowCancelModal(true)}
                >
                  <HiOutlineXMark className="btn-icon" />
                  Cancel Subscription
                </button>
              </div>
            )}
          </div>

          {/* Usage Summary */}
          {usageSummary && (
            <div className="usage-section">
              <h3>Usage Summary</h3>
              <div className="usage-grid">
                <div className="usage-card">
                  <div className="usage-header">
                    <HiOutlineChartBar className="usage-icon" />
                    <h4>Plants</h4>
                  </div>
                  <div className="usage-value">
                    {usageSummary.currentUsage?.plants || 0}
                    {usageSummary.limits?.maxPlants > 0 && (
                      <span className="usage-limit"> / {usageSummary.limits?.maxPlants}</span>
                    )}
                    {usageSummary.limits?.maxPlants === 0 && (
                      <span className="usage-unlimited"> / Unlimited</span>
                    )}
                  </div>
                  {usageSummary.limits?.maxPlants > 0 && (
                    <div className="usage-progress">
                      <div
                        className="usage-progress-bar"
                        style={{
                          width: `${getUsagePercentage(
                            usageSummary.currentUsage?.plants || 0,
                            usageSummary.limits?.maxPlants
                          )}%`
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                <div className="usage-card">
                  <div className="usage-header">
                    <HiOutlineUserGroup className="usage-icon" />
                    <h4>Users</h4>
                  </div>
                  <div className="usage-value">
                    {usageSummary.currentUsage?.users || 0}
                    {usageSummary.limits?.maxUsers > 0 && (
                      <span className="usage-limit"> / {usageSummary.limits?.maxUsers}</span>
                    )}
                    {usageSummary.limits?.maxUsers === 0 && (
                      <span className="usage-unlimited"> / Unlimited</span>
                    )}
                  </div>
                  {usageSummary.limits?.maxUsers > 0 && (
                    <div className="usage-progress">
                      <div
                        className="usage-progress-bar"
                        style={{
                          width: `${getUsagePercentage(
                            usageSummary.currentUsage?.users || 0,
                            usageSummary.limits?.maxUsers
                          )}%`
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                <div className="usage-card">
                  <div className="usage-header">
                    <HiOutlineCog className="usage-icon" />
                    <h4>Machines</h4>
                  </div>
                  <div className="usage-value">
                    {usageSummary.currentUsage?.machines || 0}
                    {usageSummary.limits?.maxMachines > 0 && (
                      <span className="usage-limit"> / {usageSummary.limits?.maxMachines}</span>
                    )}
                    {usageSummary.limits?.maxMachines === 0 && (
                      <span className="usage-unlimited"> / Unlimited</span>
                    )}
                  </div>
                  {usageSummary.limits?.maxMachines > 0 && (
                    <div className="usage-progress">
                      <div
                        className="usage-progress-bar"
                        style={{
                          width: `${getUsagePercentage(
                            usageSummary.currentUsage?.machines || 0,
                            usageSummary.limits?.maxMachines
                          )}%`
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                <div className="usage-card">
                  <div className="usage-header">
                    <HiOutlineServer className="usage-icon" />
                    <h4>Storage</h4>
                  </div>
                  <div className="usage-value">
                    {usageSummary.limits?.maxStorageGB === 0 ? (
                      <span className="usage-unlimited">Unlimited</span>
                    ) : (
                      <>
                        {usageSummary.limits?.maxStorageGB || 0}
                        <span className="usage-limit"> GB</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Change Plan Modal */}
      <SharedModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        title="Change Subscription Plan"
        customWidth="600px"
        footer={
          <div className="modal-actions" style={{width: '100%', display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
            <button
              className="btn-secondary"
              onClick={() => setShowChangePlanModal(false)}
              disabled={changing}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleChangePlan}
              disabled={!selectedPlanId || changing || !canManage}
            >
              {changing ? 'Changing...' : 'Change Plan'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <p>Select a new subscription plan:</p>
          <div className="plans-list">
            {availablePlans
              .filter(plan => plan.planId !== currentSubscription?.plan?.planId)
              .map((plan) => (
                <div
                  key={plan.planId}
                  className={`plan-option ${selectedPlanId === plan.planId ? 'selected' : ''}`}
                  onClick={() => setSelectedPlanId(plan.planId)}
                >
                  <div className="plan-option-header">
                    <h4>{plan.planName}</h4>
                    <span className="plan-price">
                      {formatCurrency(plan.price, plan.currency)}
                      <span className="plan-cycle">/{plan.billingCycle}</span>
                    </span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>
              ))}
          </div>
        </div>
      </SharedModal>

      {/* Cancel Subscription Modal */}
      <SharedModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Subscription"
        customWidth="500px"
        footer={
          <div className="modal-actions" style={{width: '100%', display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
            <button
              className="btn-secondary"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelling}
            >
              Keep Subscription
            </button>
            <button
              className="btn-danger"
              onClick={handleCancelSubscription}
              disabled={cancelling || !canManage}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </button>
          </div>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to cancel your subscription to{' '}
            <strong>{currentSubscription?.plan?.planName}</strong>?
          </p>
          <p className="warning-text">
            Your subscription will remain active until the end of the current billing period.
            After that, you will lose access to premium features.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Subscription;

